import React, { useState, useEffect, useMemo } from "react";
import Combobox from "react-widgets/Combobox";
import "react-widgets/styles.css";
import AlertMessage from "../../components/AlertMessage";
import { useAuth } from "../../context/AuthContext";
import { getAllCustomer } from "../../api/customerApi";
import { getAllServicesGroup } from "../../api/servicesGroupApi";
import { getAvailableTimeSlots } from "../../api/barberApi";
import TimeSlotPicker from "../../pages/appointment/add-appointment/TimeSlotPicker";
import Accordion from "../appointment/add-appointment/Accordion";
/**
 * Modal for a barber/manager to manually add an appointment into a calendar slot.
 *
 * Props:
 * - isOpen: boolean - controls visibility
 * - onClose: () => void
 * - date: string (yyyy-mm-dd) - the day already selected on the calendar
 * - barbers: [{ id, name }]
 * - defaultBarberId: string|number - preselected barber (e.g. the calendar column clicked)
 * - services: [{ id, name, durationMinutes }]
 * - existingAppointments: [{ id, barberId, startTime, endTime, customerName }]
 *      startTime/endTime as "HH:mm" strings, same day as `date`
 * - onSave: (payload, forceOverlap) => Promise<void>
 *      payload = { date, barberId, selectedServices, startTime, endTime, customerName }
 */
export default function AddAppointmentModal({
  isOpen,
  onClose,
  dateTime,
  barber,
  existingAppointments,
  onSave,
}) {
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [customerList, setCustomerList] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [totalTime, setTotalTime] = useState(0);
  const [timeslots, setTimeslots] = useState([]);
  const [selectedTimeslot, setSelectedTimeslot] = useState();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchCustomers();
      fetchServices();
      setSelectedServices([]);

      if (dateTime) {
        setStartTime(dateTime.time);
      }
      setEndTime("");

      setError("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedTimeslot) {
      const computedEnd = addMinutes(selectedTimeslot, totalTime);
      setStartTime(selectedTimeslot);
      setEndTime(computedEnd);
      setTimeslots([]);
      setSelectedTimeslot();
    }
  }, [selectedTimeslot]);

  const handleStartTimeChange = (newStartTime) => {
    setStartTime(newStartTime);
    setEndTime(addMinutes(newStartTime, totalTime));
  };

  const handleEndTimeChange = (newEndTime) => {
    setEndTime(newEndTime);
    setStartTime(addMinutes(newEndTime, -totalTime));
  };

  useEffect(() => {
    var totalTime = 0;
    selectedServices.forEach((s) => {
      services.forEach((group) => {
        // Find the service in the current group
        const service = group.services.find(
          (service) => service.id === s.serviceId,
        );
        if (service) {
          totalTime += service.barberServices[0].time * s.quantity;
        }
      });
    });
    setTotalTime(totalTime);
    const computedEnd = addMinutes(startTime, totalTime);
    setEndTime(computedEnd);
  }, [selectedServices]);
  const fetchNewTime = async () => {
    const result = await getAvailableTimeSlots(
      barber.id,
      user.barberShop,
      dateTime.date,
      totalTime,
    );
    if (result) {
      setTimeslots(result);
    }
    console.log(result);
  };

  const { user } = useAuth();

  const fetchCustomers = async () => {
    try {
      const result = await getAllCustomer(user.barberShop);

      if (result) {
        var customerList = result.map((b) => ({
          id: b.id,
          name: b.name,
        }));

        customerList.unshift({
          id: null,
          name: "----Select client----",
        });
        setCustomerList(customerList);
        setSelectedCustomer(customerList[0]);
      }
    } catch (err) {
      console.error("Error fetching appointments:", err);
    }
  };
  const fetchServices = async () => {
    try {
      var response = await getAllServicesGroup(user.barberShop);
      setServices(response);
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
    }
  };
  // Any existing appointment for this barber, same day, whose time range intersects the new one
  const overlaps = useMemo(() => {
    if (!startTime || !endTime) return [];
    return existingAppointments.filter((appt) => {
      return timesOverlap(startTime, endTime, appt.startTime, appt.endTime);
    });
  }, [startTime, endTime, existingAppointments]);

  const hasOverlap = overlaps.length > 0;

  function buildOverlapMessage() {
    const parts = overlaps.map(
      (o) => `${o.client} (${o.startTime}–${o.endTime})`,
    );
    return `This appointment runs ${startTime}–${endTime}, which overlaps: ${parts.join(
      ", ",
    )}.`;
  }
  const handleServiceChange = (serviceId) => {
    setSelectedServices((prev) => {
      const existing = prev.find(
        (s) =>
          s.serviceId.toLocaleLowerCase() === serviceId.toLocaleLowerCase(),
      );
      if (existing) {
        return prev.filter(
          (s) =>
            s.serviceId.toLocaleLowerCase() !== serviceId.toLocaleLowerCase(),
        );
      } else {
        return [...prev, { serviceId, quantity: 1 }];
      }
    });
  };
  const handleServiceQuantityChange = (serviceId, quantity) => {
    setSelectedServices((prev) =>
      prev.map((s) => (s.serviceId === serviceId ? { ...s, quantity } : s)),
    );
  };
  async function handleSubmit(forceOverlap) {
    if (!selectedServices || !startTime || !endTime || !selectedCustomer.id) {
      setError("Fill in customer, services and start time first.");
      return;
    }
    setSaving(true);
    try {
      await onSave(
        {
          date: dateTime.date,
          selectedServices,
          startTime,
          endTime,
          customer: selectedCustomer.id,
        },
        forceOverlap,
      );
      onClose();
    } catch (err) {
      // Backend re-checks overlap independently — surface its message if it rejects
      setError(err?.message || "Could not save the appointment.");
    } finally {
      setSaving(false);
    }
  }
  if (!isOpen) return null;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      role="dialog"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add appointment</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">Date</label>
              <input
                type="text"
                className="form-control"
                value={dateTime.date}
                disabled
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Staff: </label>
              <label className="form-label">{barber.name}</label>
            </div>

            <div className="col-6 mb-3 form-control-container ">
              <label htmlFor="client" className="form-label form-control-label">
                Client
              </label>{" "}
              <Combobox
                onChange={(value) => setSelectedCustomer(value)}
                data={customerList.slice(1)}
                dataKey="id"
                textField="name"
                value={selectedCustomer}
                containerClassName={" form-combobox "}
              />
            </div>

            <div className="col-12 form-control-container ">
              <Accordion
                servicesGroup={services}
                selectedServices={selectedServices}
                onSelectService={handleServiceChange}
                handleQuantityChange={handleServiceQuantityChange}
              />
            </div>
            <div className="col-2 form-control-container">
              <span>Total time: {totalTime}</span>
            </div>
            <div className="row">
              <div className="col mb-3">
                <label className="form-label">Start time</label>
                <input
                  type="time"
                  className="form-control"
                  value={startTime}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                />
              </div>
              <div className="col mb-3">
                <label className="form-label">End time</label>
                <input
                  type="time"
                  className="form-control"
                  value={endTime}
                  onChange={(e) => handleEndTimeChange(e.target.value)}
                />
              </div>
            </div>

            {hasOverlap && (
              <div className="alert alert-warning" role="alert">
                <p>
                  {buildOverlapMessage()} You can cancel and adjust the time, or
                  book anyway.
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={fetchNewTime}
                >
                  Load another timeslots..
                </button>
                <TimeSlotPicker
                  times={timeslots}
                  setSelectedTime={setSelectedTimeslot}
                  selectedTime={selectedTimeslot}
                />
              </div>
            )}

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>

            {hasOverlap ? (
              <button
                type="button"
                className="btn btn-warning"
                onClick={() => handleSubmit(true)}
                disabled={saving}
              >
                Book anyway
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleSubmit(false)}
                disabled={saving}
              >
                Book appointment
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- helpers ----

function addMinutes(hhmm, minutes) {
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + minutes;
  const hh = String(Math.floor((total % (24 * 60)) / 60)).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

function toMinutes(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function timesOverlap(startA, endA, startB, endB) {
  return (
    toMinutes(startA) < toMinutes(endB) && toMinutes(endA) > toMinutes(startB)
  );
}
